import React from "react";
import { countAlertTransitions, countSessions } from "./reportCounts";
import { rowTimestampMs } from "./scopedTelemetry";
import { parseUtc } from "../../services/parseUtc";
import {
  partitionSegmentsByOwner,
  type TenureScope,
  type TenureSegment,
} from "../../services/installationTenure";

/**
 * Where this sensor has been, and what happened at each place.
 *
 * The figures everywhere else on this page are clipped to the sensor's CURRENT
 * location, which is the honest thing to show but hides the record of a move
 * entirely. This section is the one place that looks across the boundary, so it
 * reads the unclipped rows on purpose.
 *
 * It costs no extra requests: the alarm and session history the page already
 * fetched carry every row, and clipping only ever produced a filtered view of
 * them (see `ScopedEnvelope.unclipped`).
 *
 * Counting uses `reportCounts`, not `unitEventsUtils`: its counters take epoch
 * bounds and parse timestamps on the same UTC basis as the tenure cutoff, while
 * the unitEventsUtils family takes "yyyy-MM-dd" and parses LOCAL. Mixing those
 * is cts_app_web#62, and a segment boundary is exactly where it would show.
 */

type Props = {
  scope: TenureScope;
  settled: boolean;
  /**
   * Names of every property this ORGANIZATION owns, used to decide which of the
   * sensor's past locations it may see. A sensor's history follows the serial
   * across owners, so without this a resold unit would show the previous
   * owner's property and unit. See `partitionSegmentsByOwner`.
   */
  ownedGroupNames: string[];
  /** Unclipped alarm rows -- every location's. */
  alarmRows: unknown[];
  /** Unclipped cooking-session rows. */
  sessionRows: unknown[];
  /** Page size the rows were fetched with, so we can tell a total from a floor. */
  alarmCap: number;
  sessionCap: number;
};

function formatDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Human span for a tenure. Deliberately coarse: the question a reader has is
 * "roughly how long was it there", and a figure like "7 weeks" answers it
 * without implying we know the install happened at 14:32.
 */
function formatSpan(startedAt: number, endedAt: number | null): string {
  const days = Math.max(
    0,
    Math.round(((endedAt ?? Date.now()) - startedAt) / 86_400_000)
  );
  if (days < 1) return "less than a day";
  if (days === 1) return "1 day";
  if (days < 21) return `${days} days`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `${weeks} weeks`;
  const months = Math.round(days / 30.44);
  if (months < 24) return `${months} months`;
  return `${(days / 365.25).toFixed(1)} years`;
}

/** Oldest row we actually hold, or null when there are none. */
function oldestRowMs(rows: unknown[]): number | null {
  let oldest: number | null = null;
  for (const row of rows) {
    const r = row as Record<string, unknown>;
    const at =
      parseUtc(r?.dateCreated) ??
      parseUtc(r?.datetime) ??
      parseUtc(r?.dateStart) ??
      parseUtc(r?.startDatetime);
    if (at === null) continue;
    if (oldest === null || at < oldest) oldest = at;
  }
  return oldest;
}

/**
 * Rewrite each row's timestamp field to the instant the segment boundaries are
 * drawn on, so both halves of this feature agree about when a row happened.
 *
 * `reportCounts` resolves a row's time from the DEVICE clock -- `datetime` for
 * alarms, `startDatetime ?? dateStart` for sessions. Segment boundaries come
 * from installation rows stamped `DateTime.UtcNow` by the server, and the
 * clipping that produces the figures above uses `rowTimestampMs`, which prefers
 * the server's `dateCreated` for exactly that reason. Left alone, a sensor with
 * a skewed RTC -- the case rowTimestampMs exists to defend against -- lands in
 * one tenure here and a different one in the Alerts tile.
 *
 * Restamping rather than reimplementing the counters: what counts as a danger
 * transition is `reportCounts`' business and is held to a cross-repo golden
 * fixture. Only the clock is ours.
 */
function restampForCounting<T>(rows: T[], field: "datetime" | "startDatetime"): T[] {
  return rows.map((row) => {
    const at = rowTimestampMs(row);
    if (at === null) return row;
    return { ...(row as object), [field]: new Date(at).toISOString() } as T;
  });
}

function Note({ children }: { children: React.ReactNode }) {
  return <p className="mt-4 text-sm italic text-ink-3">{children}</p>;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-card bg-white text-ink p-6 shadow-card border border-black/5">
      <h2 className="text-xl font-semibold mb-1">Location history</h2>
      {children}
    </div>
  );
}

export default function SensorLocationHistoryCard({
  scope,
  settled,
  ownedGroupNames,
  alarmRows,
  sessionRows,
  alarmCap,
  sessionCap,
}: Props) {
  if (!settled) {
    return (
      <Shell>
        <p className="text-sm text-ink-3 py-6">Loading install history…</p>
      </Shell>
    );
  }

  // A read that FAILED is not a sensor with no record, and the two must not be
  // worded the same way: one is a fact about the sensor, the other about us.
  if (scope.reason === "fetch-failed") {
    return (
      <Shell>
        <p className="text-sm text-alert-warning py-4">
          We couldn't load this sensor's install history, so the figures on this
          page are not scoped to its current location. This is a loading problem,
          not a statement about the sensor.
        </p>
      </Shell>
    );
  }

  if (scope.reason !== "known" || scope.segments.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-ink-2 py-4">
          No installation record for this sensor, so the figures on this page
          cover its whole recorded history and may span more than one location.
        </p>
        <Note>
          A record is written when a sensor is installed from the mobile app.
          Sensors installed before that was captured, or moved by re-assigning
          their property group, have nothing to show here.
        </Note>
      </Shell>
    );
  }

  // A sensor's history follows the serial across owners, so a resold unit
  // arrives carrying somebody else's property and unit number. Withhold every
  // tenure this organization cannot claim, before anything is counted or drawn.
  const { owned, withheld } = partitionSegmentsByOwner(
    scope.segments,
    ownedGroupNames
  );

  // Reaching here means `reason` is "known" with at least one segment, so an
  // empty `owned` necessarily means every one of them was withheld.
  if (owned.length === 0) {
    return (
      <Shell>
        <p className="text-sm text-ink-2 py-4">
          This sensor's recorded locations all predate your organization's
          ownership of it, so none are shown.
        </p>
      </Shell>
    );
  }

  const oldestAlarm = oldestRowMs(alarmRows);
  const oldestSession = oldestRowMs(sessionRows);
  const alarmsCapped = alarmRows.length >= alarmCap;
  const sessionsCapped = sessionRows.length >= sessionCap;

  // Oldest first, so the rows read as a sequence. The open tenure identifies
  // itself by reading "now" in the Until column.
  const countableAlarms = restampForCounting(alarmRows, "datetime");
  const countableSessions = restampForCounting(sessionRows, "startDatetime");

  const rows = owned.map((segment: TenureSegment) => {
    const end = segment.endedAt ?? Date.now();
    const counts = countAlertTransitions(
      countableAlarms as never,
      segment.startedAt,
      end
    );
    const sessions = countSessions(
      countableSessions as never,
      segment.startedAt,
      end
    );
    // Our history is capped at a page size. If this segment began before the
    // oldest row we hold, its counts are floors, not totals -- say so rather
    // than printing a number that reads like the whole truth.
    const alarmFloor =
      alarmsCapped && oldestAlarm !== null && segment.startedAt < oldestAlarm;
    const sessionFloor =
      sessionsCapped && oldestSession !== null && segment.startedAt < oldestSession;
    return {
      segment,
      counts,
      sessions,
      alarmFloor,
      sessionFloor,
    };
  });

  const anyFloor = rows.some((r) => r.alarmFloor || r.sessionFloor);
  const anyRepeat = rows.some((r) => r.segment.rowCount > 1);

  return (
    <Shell>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-3">
              <th className="pb-2 pr-4 font-medium">Location</th>
              <th className="pb-2 pr-4 font-medium">From</th>
              <th className="pb-2 pr-4 font-medium">Until</th>
              <th className="pb-2 pr-4 font-medium">For</th>
              <th className="pb-2 pr-4 font-medium text-right">Cooking sessions</th>
              <th className="pb-2 pr-4 font-medium text-right">Danger alerts</th>
              <th className="pb-2 font-medium text-right">Safety shut offs</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.segment.startedAt}`}
                className="border-b border-ink-3/15 last:border-0"
              >
                <td className="py-2 pr-4">
                  <span className="font-medium">
                    {row.segment.address ?? "Location not recorded"}
                  </span>
                  {row.segment.rowCount > 1 && (
                    <span className="ml-2 text-xs text-ink-3">
                      re-installed {row.segment.rowCount - 1}&times;
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4 whitespace-nowrap">
                  {formatDay(row.segment.startedAt)}
                </td>
                <td className="py-2 pr-4 whitespace-nowrap">
                  {row.segment.endedAt === null
                    ? "now"
                    : formatDay(row.segment.endedAt)}
                </td>
                <td className="py-2 pr-4 whitespace-nowrap text-ink-2">
                  {formatSpan(row.segment.startedAt, row.segment.endedAt)}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {row.sessionFloor ? "at least " : ""}
                  {row.sessions}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {row.alarmFloor ? "at least " : ""}
                  {row.counts.danger}
                </td>
                <td className="py-2 text-right tabular-nums">
                  {row.alarmFloor ? "at least " : ""}
                  {row.counts.shutoffs}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {withheld > 0 && (
        <Note>
          {withheld} earlier {withheld === 1 ? "period is" : "periods are"} not
          shown, because {withheld === 1 ? "it" : "they"} could not be attributed
          to a property your organization owns.
        </Note>
      )}

      {anyFloor && (
        <Note>
          We hold the most recent {alarmCap.toLocaleString()} alert records and{" "}
          {sessionCap.toLocaleString()} sessions for this sensor. Periods that
          begin before the oldest record we hold are shown as "at least".
        </Note>
      )}

      {anyRepeat && (
        <Note>
          A sensor re-installed at the same address counts as one period, not a
          move, so a repair does not split its history.
        </Note>
      )}
    </Shell>
  );
}
